import { NovaSpinner } from "@nova/component-nova-spinner"
import classNames from "classnames"
import { mapExtensionsToMimeType } from "features/job-management/uploader/constants"
import { DragEventHandler, HTMLAttributes, useRef } from "react"
import * as Icons from "@nova/component-icons"
import { useDropBox, UseDropBoxProps } from "./useDropBox"
import { FileExtension } from "@nova/constants"

export const DropBoxV2 = ({
    canAcceptMultiple = false,
    allowedFileExtensions,
    onNewItems,
    isProcessing,
    horizontalPaddingInRem,
    maxFileSizeInMB,
    title,
    hasProcessingError = false,
    ...rest
}: {
    innerClassName?: string
    horizontalPaddingInRem?: number
    maxFileSizeInMB?: number
    title?: string
} & UseDropBoxProps &
    HTMLAttributes<HTMLDivElement>) => {
    const {
        uploadRef,
        hasUploadError,
        canDrop,
        setCanDrop,
        processingText,
        handleInputChange,
        fileTypeList,
        formattedFileTypes,
        isOver,
        dropRef,
    } = useDropBox({ canAcceptMultiple, allowedFileExtensions, onNewItems, isProcessing })

    const hasError = hasUploadError || hasProcessingError

    const handleDragOver: DragEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault()
        const rawItems = e.dataTransfer?.items

        const items = [...rawItems]

        const newVal = items.every((item) =>
            Object.entries(mapExtensionsToMimeType).find(
                ([key, value]) =>
                    item.type === value && allowedFileExtensions.includes(key as FileExtension)
            )
        )

        setCanDrop(newVal)
    }
    const ref = useRef(null)
    dropRef(ref)
    return (
        <div className={classNames("select-none", rest.className)}>
            {!isProcessing ? (
                <div
                    ref={ref}
                    className={classNames(
                        "relative w-full py-6 border rounded-md flex flex-col items-center",
                        {
                            "px-4": horizontalPaddingInRem === undefined,
                            "border-dashed": !hasError,
                            "border-red-500": hasError || (isOver && !canDrop),
                            "border-white-500": !hasError && !isOver,
                            "border-blue-500": isOver && canDrop,
                        }
                    )}
                    style={{
                        paddingLeft: horizontalPaddingInRem && horizontalPaddingInRem + "rem",
                        paddingRight: horizontalPaddingInRem && horizontalPaddingInRem + "rem",
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={() => setCanDrop(false)}
                >
                    <Icons.Icon Icon={Icons.Backup} className="text-gray-0 mb-4" />
                    <h4 className="text-gray-0 mb-2">
                        {hasUploadError
                            ? "Incorrect File Type"
                            : title
                              ? title
                              : "Upload Shapes & PDFs"}
                    </h4>
                    <h5 className="mb-1">
                        <span
                            className="text-blue-200 hover:text-blue-500 underline cursor-pointer"
                            onClick={() => uploadRef.current?.click()}
                        >
                            {canAcceptMultiple ? "Choose files" : "Choose a file"}
                        </span>
                        <span className="text-gray-400">
                            {` or drag ${canAcceptMultiple ? "them" : "it"} here`}
                        </span>
                    </h5>
                    {!isOver && (
                        <>
                            <h5 className="text-gray-400">{fileTypeList}</h5>
                            {maxFileSizeInMB && (
                                <h5 className="text-gray-400">{`Max file size: ${maxFileSizeInMB} MB`}</h5>
                            )}
                        </>
                    )}
                    <input
                        ref={uploadRef}
                        type="file"
                        multiple={canAcceptMultiple}
                        accept={formattedFileTypes}
                        onChange={handleInputChange}
                        className="hidden"
                    />
                </div>
            ) : (
                <div className="flex gap-2.5 p-4 items-center">
                    <NovaSpinner width={24} height={24} />
                    <h4 className="text-gray-0">{processingText}</h4>
                </div>
            )}
        </div>
    )
}
